'use client'

import { useState, useMemo } from 'react'
import Link from 'next/link'

type AnyDoc = Record<string, any>

function mediaUrl(val: any): string | undefined {
  if (!val) return undefined
  if (typeof val === 'string') return val
  if (val.url) return val.url
  if (val.filename) return `/api/media/file/${encodeURIComponent(val.filename)}`
  return undefined
}

type Category = 'upcoming' | 'ongoing' | 'past'

export interface EventStrings {
  headerLabel: string; title: string; subtitle: string
  tabAll: string; tabUpcoming: string; tabOngoing: string; tabPast: string
  statusUpcoming: string; statusOngoing: string; statusPast: string
  cardUntil: string; cardDetails: string; noEvents: string
  months: string[]
}

function categorize(event: AnyDoc, nowMs: number): Category {
  const start = event.startDate ? new Date(event.startDate).getTime() : null
  const end   = event.endDate   ? new Date(event.endDate).getTime()   : null
  if (start === null) return 'upcoming'
  if (end !== null) { if (end < nowMs) return 'past'; if (start <= nowMs) return 'ongoing'; return 'upcoming' }
  const sd = new Date(event.startDate), nd = new Date(nowMs)
  const same = sd.getUTCFullYear()===nd.getUTCFullYear() && sd.getUTCMonth()===nd.getUTCMonth() && sd.getUTCDate()===nd.getUTCDate()
  if (same) return 'ongoing'; if (start < nowMs) return 'past'; return 'upcoming'
}

function EventCard({ event, category, strings }: { event: AnyDoc; category: Category; strings: EventStrings }) {
  const img   = mediaUrl(event.coverImage)
  const start = event.startDate ? new Date(event.startDate) : null
  const end   = event.endDate   ? new Date(event.endDate)   : null
  const badgeClass = `evl-status evl-status--${category}`
  const badgeLabel = category==='upcoming' ? strings.statusUpcoming : category==='ongoing' ? strings.statusOngoing : strings.statusPast
  const endStr = end ? strings.cardUntil
    .replace('{month}', strings.months[end.getUTCMonth()])
    .replace('{day}',   String(end.getUTCDate()))
    .replace('{year}',  String(end.getUTCFullYear())) : null

  return (
    <Link href={`/events/${event.slug}`} className="evl-card">
      <div className={`evl-cover${!img ? ' grad-event' : ''}`}>
        {img && <img src={img} alt={event.title ?? ''} />}
        <div className="evl-cover-overlay" />
        {start && (
          <div className="evl-datebox">
            <div className="evl-datebox-mon">{strings.months[start.getUTCMonth()]}</div>
            <div className="evl-datebox-day">{start.getUTCDate()}</div>
            <div className="evl-datebox-year">{start.getUTCFullYear()}</div>
          </div>
        )}
        <div className={badgeClass}>{badgeLabel}</div>
      </div>
      <div className="evl-body">
        <div className="evl-title">{event.title}</div>
        {event.locationName && <div className="evl-loc">{'\u25ce'} {event.locationName}</div>}
        {endStr && <div className="evl-enddate">{endStr}</div>}
        <div className="evl-cta">{strings.cardDetails}</div>
      </div>
    </Link>
  )
}

export function EventsListClient({ events, strings }: { events: AnyDoc[]; strings: EventStrings }) {
  type Tab = Category | 'all'
  const [tab, setTab] = useState<Tab>('upcoming')
  const nowMs = Date.now()

  const categorized = useMemo(() => {
    const map: Record<Category, AnyDoc[]> = { upcoming: [], ongoing: [], past: [] }
    for (const ev of events) map[categorize(ev, nowMs)].push(ev)
    return map
  }, [events, nowMs])

  const counts: Record<Tab, number> = {
    all: events.length, upcoming: categorized.upcoming.length,
    ongoing: categorized.ongoing.length, past: categorized.past.length,
  }
  const TABS: { key: Tab; label: string }[] = [
    { key: 'all', label: strings.tabAll }, { key: 'upcoming', label: strings.tabUpcoming },
    { key: 'ongoing', label: strings.tabOngoing }, { key: 'past', label: strings.tabPast },
  ]
  const displayed = tab === 'all' ? events : categorized[tab as Category]
  const sorted = [...displayed].sort((a, b) => {
    const am = a.startDate ? new Date(a.startDate).getTime() : 0
    const bm = b.startDate ? new Date(b.startDate).getTime() : 0
    return tab === 'past' ? bm - am : am - bm
  })

  return (
    <div className="listing-page evl-page">
      <div className="evl-header">
        <div className="evl-header-label">{strings.headerLabel}</div>
        <h1 className="evl-heading">{strings.title}</h1>
        <p className="evl-subheading">{strings.subtitle}</p>
      </div>
      <div className="evl-tabs" role="tablist">
        {TABS.map(({ key, label }) => (
          <button key={key} role="tab" aria-selected={tab===key}
            className={`evl-tab${tab===key ? ' evl-tab--active' : ''}`}
            onClick={() => setTab(key)}>
            {label}{counts[key] > 0 && <span className="evl-tab-count">{counts[key]}</span>}
          </button>
        ))}
      </div>
      {sorted.length === 0 ? (
        <div className="evl-empty">{strings.noEvents.replace('{status}', tab==='all' ? '' : tab)}</div>
      ) : (
        <div className="evl-grid">
          {sorted.map(ev => <EventCard key={ev.id} event={ev} category={categorize(ev, nowMs)} strings={strings} />)}
        </div>
      )}
    </div>
  )
}
